import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Alert, Button, Accordion, ButtonGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCloud, 
  faLink, 
  faSignOutAlt, 
  faExclamationTriangle, 
  faHome, 
  faChevronRight,
  faSync,
  faToggleOn
} from '@fortawesome/free-solid-svg-icons';
import './PilotDashboard.css';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

import djiBridge from '../utils/cloud-sample-DjiBridge';
import cloudModule, { EComponentName, EStatusValue } from '../utils/cloud-sample-cloudModule';
import { ELocalStorageKey } from '../utils/constants';

/* eslint-disable no-undef */

// Configuration for the Pilot dashboard that can be dynamically updated
const CURRENT_CONFIG = {
  token: localStorage.getItem(ELocalStorageKey.Token),
  username: localStorage.getItem(ELocalStorageKey.Username),
  userId: localStorage.getItem(ELocalStorageKey.UserId),
  websocketURL: localStorage.getItem(ELocalStorageKey.WsUrl)
};

// API endpoints using relative paths through nginx proxy
const USER_INFO_URL = `/manage/api/v1/users/current`;
const PLATFORM_INFO_URL = `/manage/api/v1/workspaces/current`;
const DEVICE_INFO_URL = (workspaceId, deviceSn) => `/manage/api/v1/workspaces/${workspaceId}/devices/${deviceSn}`;
const BIND_DEVICE_URL = (deviceSn) => `/manage/api/v1/devices/${deviceSn}/binding`;

/**
 * Cloud Sample PilotDashboard Component
 * Following exact implementation of pilot-home.vue
 */
const CloudSamplePilotDashboard = () => {
  const navigate = useNavigate();
  
  // Define localStorage keys exactly as in pilot-home.vue - MOVED TO TOP
  const ELocalStorageKey = {
    Token: 'x-auth-token',
    Username: 'username',
    UserId: 'user_id',
    WorkspaceId: 'workspace_id',
    WorkspaceName: 'workspace_name',
    WorkspaceDesc: 'workspace_desc',
    PlatformName: 'platform_name',
    GatewayOnline: 'gatewayOnline',
    Device: 'device',
    Flag: 'flag',
    WebsdkNgInstallId: 'websdk_ng_install_id',
    WsUrl: 'wsUrl'
  };
  
  const [username, setUsername] = useState(localStorage.getItem(ELocalStorageKey.Username) || '');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [exitVisible, setExitVisible] = useState(false);
  const [workspaceName, setWorkspaceName] = useState(localStorage.getItem(ELocalStorageKey.WorkspaceName) || '');
  const [wsId, setWsId] = useState(localStorage.getItem(ELocalStorageKey.WorkspaceId) || '');
  
  // Connection states - exactly as in pilot-home.vue
  const [thingState, setThingState] = useState(EStatusValue.DISCONNECT);
  const [apiState, setApiState] = useState(EStatusValue.DISCONNECT);
  const [wsState, setWsState] = useState(EStatusValue.DISCONNECT);
  const [liveState, setLiveState] = useState(EStatusValue.DISCONNECT);
  const [mapState, setMapState] = useState(EStatusValue.DISCONNECT);
  const [tsaState, setTsaState] = useState(EStatusValue.DISCONNECT);
  const [mediaState, setMediaState] = useState(EStatusValue.DISCONNECT);
  const [waylineState, setWaylineState] = useState(EStatusValue.DISCONNECT);
  
  // Components map to match pilot-home.vue's components handling
  const [components, setComponents] = useState(new Map());
  
  // Device information - exactly matching structure in pilot-home.vue
  const [device, setDevice] = useState({
    data: {
      sn: '',
      online_status: false,
      device_callsign: '',
      user_id: '',
      user_callsign: '',
      bound_status: false,
      model: '',
      gateway_sn: EStatusValue.DISCONNECT,
      domain: -1
    }
  });
  
  // Binding parameters - exactly matching structure in pilot-home.vue
  const [bindParam, setBindParam] = useState({
    device_sn: '',
    user_id: '',
    workspace_id: wsId
  });
  
  // Module list for drawer - exactly matching structure in pilot-home.vue
  const modules = [
    { name: 'Cloud', state: thingState, module: EComponentName.Thing },
    { name: 'Api', state: apiState, module: EComponentName.Api },
    { name: 'Live', state: liveState, module: EComponentName.Liveshare },
    { name: 'Ws', state: wsState, module: EComponentName.Ws },
    { name: 'Map', state: mapState, module: EComponentName.Map },
    { name: 'Tsa', state: tsaState, module: EComponentName.Tsa },
    { name: 'Media', state: mediaState, module: EComponentName.Media },
    { name: 'Wayline', state: waylineState, module: EComponentName.Mission }
  ];
  
  /**
   * Handle API error responses, particularly 401 Unauthorized
   * @param {Error} error - The error from an API call
   */
  const handleApiError = async (error, navigateFunc) => {
    console.error('[CLOUD-SAMPLE] API error:', error);
    console.log('[CLOUD-SAMPLE] Error response:', error.response ? error.response.data : 'No response data');
    
    // If unauthorized (401), token is expired or invalid - redirect to login
    if (error.response && error.response.status === 401) {
      console.log('[CLOUD-SAMPLE] Received 401 Unauthorized, redirecting to login');
      
      // IMPORTANT: Only clear token from localStorage, not from DJI Pilot
      // We should never call apiSetToken with empty string as it could break the module
      localStorage.removeItem(ELocalStorageKey.Token);
      console.log('[CLOUD-SAMPLE] Cleared invalid token from localStorage only');
      
      // Redirect to login - token refresh will be handled by PilotLogin
      navigateFunc('/cloud-sample-login');
    }
    
    return error;
  };
  
  // Initialize dashboard when component mounts
  // Following exact sequence from pilot-home.vue onMounted function
  useEffect(() => {
    console.log('[CLOUD-SAMPLE] Initializing dashboard');
    
    // Register callbacks and back click handler exactly as in pilot-home.vue
    djiBridge.onBackClickReg();
    djiBridge.onStopPlatform();
    
    // Define global callbacks for DJI bridge
    window.connectCallback = (arg) => {
      connectCallback(arg);
    };
    window.wsConnectCallback = (arg) => {
      wsConnectCallback(arg);
    };
    
    // Load device data from localStorage if available
    const oldDevice = localStorage.getItem(ELocalStorageKey.Device);
    if (oldDevice) {
      try {
        setDevice({ data: JSON.parse(oldDevice) });
      } catch (err) {
        console.error('[CLOUD-SAMPLE] Error parsing device data:', err);
      }
    }
    
    // Get device info from bridge exactly as in pilot-home.vue
    const gatewaySn = djiBridge.getRemoteControllerSN();
    if (gatewaySn === EStatusValue.DISCONNECT.toString()) {
      setError('Data is not available, please restart the remote control.');
      return;
    }
    
    // Update device data with SN information
    setDevice(prev => ({
      data: {
        ...prev.data,
        gateway_sn: gatewaySn,
        sn: djiBridge.getAircraftSN()
      }
    }));
    
    // Update bind parameters with the gateway SN - do this once during initialization
    setBindParam(prev => ({
      ...prev,
      device_sn: gatewaySn
    }));
    
    // Load Thing module directly using stored MQTT configuration from login
    console.log('[CLOUD-SAMPLE] Loading Thing module after getting device SN');
    try {
      const storedMqttConfig = localStorage.getItem('mqttConfig');
      if (storedMqttConfig) {
        const mqttParams = JSON.parse(storedMqttConfig);
        console.log('[CLOUD-SAMPLE] Setting Thing component parameters with stored MQTT config:', {
          host: mqttParams.host,
          username: mqttParams.username,
          password: '****',
          connectCallback: mqttParams.connectCallback
        });
        
        // Set Thing component parameters
        djiBridge.setComponentParam(EComponentName.Thing, mqttParams);
        
        // Load Thing component using the exact same pattern as for API module
        console.log('[CLOUD-SAMPLE] Loading Thing component');
        const thingLoaded = djiBridge.loadComponent(
          EComponentName.Thing, 
          djiBridge.getComponentParam(EComponentName.Thing)
        );
        console.log('[CLOUD-SAMPLE] Thing component load result:', thingLoaded);
      } else {
        console.error('[CLOUD-SAMPLE] No MQTT config found in localStorage');
        setError('No MQTT connection information available');
      }
    } catch (err) {
      console.error('[CLOUD-SAMPLE] Error loading Thing component:', err);
      setError('Failed to load MQTT component');
    }
    
    // Set workspace info
    setWorkspaceInfo();
    
    // Get user info and complete the rest of the setup
    getUserInfo()
      .then(res => {
        if (!res || !res.data) {
          setError('Failed to get user info');
          setLoading(false);
          return;
        }
        
        // Set username and update localStorage
        setUsername(res.data.username);
        localStorage.setItem(ELocalStorageKey.Username, res.data.username);
        
        // Update bind parameters
        setBindParam({
          device_sn: device.data.gateway_sn,
          user_id: res.data.user_id,
          workspace_id: res.data.workspace_id
        });
        
        setLoading(false);
      })
      .catch(err => {
        console.error('[CLOUD-SAMPLE] Error getting user info:', err);
        setError('Failed to get user info');
        setLoading(false);
      });
    
    // Get device info
    setTimeout(getDeviceInfo, 3000);
  }, []);

  /**
   * Get platform information from the server - exactly as in pilot-home.vue
   */
  const setWorkspaceInfo = async () => {
    // If workspace info already exists in localStorage, use it
    if (localStorage.getItem(ELocalStorageKey.WorkspaceName)) {
      djiBridge.platformSetInformation(
        localStorage.getItem(ELocalStorageKey.PlatformName) || '',
        localStorage.getItem(ELocalStorageKey.WorkspaceName) || '',
        localStorage.getItem(ELocalStorageKey.WorkspaceDesc) || ''
      );
      djiBridge.setWorkspaceId(wsId);
      return;
    }
    
    // Otherwise fetch from API
    try {
      const token = localStorage.getItem(ELocalStorageKey.Token);
      const response = await axios.get(PLATFORM_INFO_URL, {
        headers: { 'x-auth-token': token }
      });
      
      console.log('[CLOUD-SAMPLE] Platform info response:', response);
      
      if (response.data && response.data.code === 0 && response.data.data) {
        const platformData = response.data.data;
        setWorkspaceName(platformData.workspace_name);
        setWsId(platformData.workspace_id);
        
        localStorage.setItem(ELocalStorageKey.PlatformName, platformData.platform_name);
        localStorage.setItem(ELocalStorageKey.WorkspaceName, platformData.workspace_name);
        localStorage.setItem(ELocalStorageKey.WorkspaceDesc, platformData.workspace_desc);
        
        djiBridge.platformSetInformation(
          platformData.platform_name,
          platformData.workspace_name,
          platformData.workspace_desc
        );
        djiBridge.setWorkspaceId(platformData.workspace_id);
      }
    } catch (error) {
      console.error('[CLOUD-SAMPLE] Error fetching platform information:', error);
    }
  };
  
  /**
   * Get user information - exactly as in pilot-home.vue
   */
  const getUserInfo = async () => {
    try {
      const token = localStorage.getItem(ELocalStorageKey.Token);
      console.log('[CLOUD-SAMPLE] Getting user info with token:', token ? token.substring(0, 10) + '...' : 'null');
      
      const response = await axios.get(USER_INFO_URL, {
        headers: { 'x-auth-token': token }
      });
      
      return response.data;
    } catch (error) {
      console.error('[CLOUD-SAMPLE] Error fetching user information:', error);
      throw error;
    }
  };
  
  /**
   * Get device information - exactly as in pilot-home.vue
   */
  const getDeviceInfo = async () => {
    if (!device.data.sn || device.data.sn === EStatusValue.DISCONNECT) {
      return;
    }
    
    try {
      const token = localStorage.getItem(ELocalStorageKey.Token);
      const response = await axios.get(DEVICE_INFO_URL(bindParam.workspace_id, device.data.sn), {
        headers: { 'x-auth-token': token }
      });
      
      if (response.data.code !== 0) {
        return;
      }
      
      setDevice(prev => ({
        data: {
          ...prev.data,
          online_status: response.data.data.status,
          bound_status: response.data.data.bound_status,
          device_callsign: response.data.data.nickname,
          model: response.data.data.device_name
        }
      }));
      
      localStorage.setItem(ELocalStorageKey.Device, JSON.stringify(device.data));
    } catch (error) {
      console.error('[CLOUD-SAMPLE] Error fetching device information:', error);
    }
  };
  
  /**
   * Refresh status - exactly as in pilot-home.vue refreshStatus function
   */
  const refreshStatus = () => {
    setThingState(djiBridge.thingGetConnectState() ? EStatusValue.CONNECTED : EStatusValue.DISCONNECT);
    setApiState(djiBridge.isComponentLoaded(EComponentName.Api) ? EStatusValue.CONNECTED : EStatusValue.DISCONNECT);
    setLiveState(djiBridge.isComponentLoaded(EComponentName.Liveshare) ? EStatusValue.CONNECTED : EStatusValue.DISCONNECT);
    setWsState(djiBridge.isComponentLoaded(EComponentName.Ws) && djiBridge.wsGetConnectState()
      ? EStatusValue.CONNECTED
      : EStatusValue.DISCONNECT);
    setMapState(djiBridge.isComponentLoaded(EComponentName.Map) ? EStatusValue.CONNECTED : EStatusValue.DISCONNECT);
    setTsaState(djiBridge.isComponentLoaded(EComponentName.Tsa) ? EStatusValue.CONNECTED : EStatusValue.DISCONNECT);
    setMediaState(djiBridge.isComponentLoaded(EComponentName.Media) ? EStatusValue.CONNECTED : EStatusValue.DISCONNECT);
    setWaylineState(djiBridge.isComponentLoaded(EComponentName.Mission) ? EStatusValue.CONNECTED : EStatusValue.DISCONNECT);
  };
  
  /**
   * Module installation - exactly as in pilot-home.vue moduleInstall function
   */
  const moduleInstall = (m) => {
    console.log(`[CLOUD-SAMPLE] Installing ${m.name} module`);
    
    let param;
    switch (m.module) {
      case EComponentName.Thing:
        param = djiBridge.thingGetConfigs();
        break;
      case EComponentName.Api: {
        param = {
          host: djiBridge.getHost(),
          token: djiBridge.getToken()
        };
        break;
      }
      case EComponentName.Map: {
        const mapParams = {
          userName: localStorage.getItem(ELocalStorageKey.Username) || '',
          elementPreName: 'PILOT'
        };
        djiBridge.setComponentParam(EComponentName.Map, mapParams);
        console.log(`[CLOUD-SAMPLE] Map module parameters set:`, mapParams);
        param = djiBridge.getComponentParam(EComponentName.Map);
        break;
      }
      case EComponentName.Ws: {
        const token = localStorage.getItem(ELocalStorageKey.Token);
        const wsUrl = CURRENT_CONFIG.websocketURL;
        
        if (!wsUrl) {
          console.error('[CLOUD-SAMPLE] No WebSocket URL found in localStorage');
          return;
        }
        
        const wsParams = {
          host: wsUrl,
          token: token,
          connectCallback: 'wsConnectCallback'
        };
        
        console.log('[CLOUD-SAMPLE] WS parameters:', wsParams);
        djiBridge.setComponentParam(EComponentName.Ws, wsParams);
        param = djiBridge.getComponentParam(EComponentName.Ws);
        break;
      }
      default:
        param = djiBridge.getComponentParam(m.module);
    }
    
    djiBridge.setComponentParam(m.module, param);
    console.log(`[CLOUD-SAMPLE] ${m.name} module parameters:`, param);
    
    djiBridge.loadComponent(m.module, djiBridge.getComponentParam(m.module));
    refreshStatus();
  };
  
  /**
   * Module uninstallation - exactly as in pilot-home.vue moduleUninstall function
   */
  const moduleUninstall = (m) => {
    console.log(`[CLOUD-SAMPLE] Uninstalling ${m.name} module`);
    djiBridge.unloadComponent(m.module);
    refreshStatus();
  };
  
  /**
   * MQTT connection callback - exactly as in pilot-home.vue connectCallback function
   */
  const connectCallback = async (arg) => {
    console.log('[CLOUD-SAMPLE] Connect callback invoked with arg:', arg);
    
    if (arg) {
      setThingState(EStatusValue.CONNECTED);
      
      // First make sure API component is loaded with valid token
      if (!djiBridge.isComponentLoaded(EComponentName.Api)) {
        console.log('[CLOUD-SAMPLE] API component not loaded, checking token and loading component');
        
        // Verify token and load API component
        const isTokenValid = await verifyTokenValidity(navigate);
        if (!isTokenValid) {
          console.error('[CLOUD-SAMPLE] Token validation failed during MQTT connect callback');
          // verifyTokenValidity handles the redirection to login if token is invalid
          return;
        }
        
        // If we reach here, token is valid and API component should be loaded
        console.log('[CLOUD-SAMPLE] Token is valid and API component is loaded');
      }
      
      // Load Liveshare component - exactly as in pilot-home.vue
      console.log('[CLOUD-SAMPLE] Loading Liveshare component');
      const liveshareResult = djiBridge.loadComponent(
        EComponentName.Liveshare, 
        djiBridge.getComponentParam(EComponentName.Liveshare)
      );
      console.log('[CLOUD-SAMPLE] Liveshare component load result:', liveshareResult);
      
      // Set up and load WS component - exactly as in pilot-home.vue
      console.log('[CLOUD-SAMPLE] Setting WS component parameters');
      const token = localStorage.getItem(ELocalStorageKey.Token);
      const wsParams = {
        host: CURRENT_CONFIG.websocketURL, // Using fixed format: ws://192.168.178.24:8083
        token: token, // Token will be encoded and spliced into x-auth-token
        connectCallback: 'wsConnectCallback'
      };
      djiBridge.setComponentParam(EComponentName.Ws, wsParams);
      
      console.log('[CLOUD-SAMPLE] Loading WS component with parameters:', {
        host: wsParams.host,
        token: wsParams.token ? `${wsParams.token.substring(0, 10)}...` : 'null',
        connectCallback: wsParams.connectCallback
      });
      const wsResult = djiBridge.loadComponent(
        EComponentName.Ws, 
        djiBridge.getComponentParam(EComponentName.Ws)
      );
      console.log('[CLOUD-SAMPLE] WS component load result:', wsResult);
      
      // Set up and load Map component - exactly as in pilot-home.vue
      console.log('[CLOUD-SAMPLE] Setting Map component parameters');
      const mapParams = {
        userName: username,
        elementPreName: 'PILOT'
      };
      djiBridge.setComponentParam(EComponentName.Map, mapParams);
      
      console.log('[CLOUD-SAMPLE] Loading Map component with parameters:', mapParams);
      const mapResult = djiBridge.loadComponent(
        EComponentName.Map, 
        djiBridge.getComponentParam(EComponentName.Map)
      );
      console.log('[CLOUD-SAMPLE] Map component load result:', mapResult);
      
      // Load TSA component - exactly as in pilot-home.vue
      console.log('[CLOUD-SAMPLE] Loading TSA component');
      const tsaResult = djiBridge.loadComponent(
        EComponentName.Tsa, 
        djiBridge.getComponentParam(EComponentName.Tsa)
      );
      console.log('[CLOUD-SAMPLE] TSA component load result:', tsaResult);
      
      // Load Media component - exactly as in pilot-home.vue
      console.log('[CLOUD-SAMPLE] Loading Media component');
      const mediaResult = djiBridge.loadComponent(
        EComponentName.Media, 
        djiBridge.getComponentParam(EComponentName.Media)
      );
      console.log('[CLOUD-SAMPLE] Media component load result:', mediaResult);
      
      // Set download owner
      console.log('[CLOUD-SAMPLE] Setting download owner to 1 (Mine)');
      const downloadOwnerResult = djiBridge.setDownloadOwner(1); // EDownloadOwner.Mine.valueOf()
      console.log('[CLOUD-SAMPLE] Set download owner result:', downloadOwnerResult);
      
      // Load Mission component - exactly as in pilot-home.vue
      console.log('[CLOUD-SAMPLE] Loading Mission component');
      const missionResult = djiBridge.loadComponent(EComponentName.Mission, {});
      console.log('[CLOUD-SAMPLE] Mission component load result:', missionResult);
      
      // Start device binding loop exactly as in pilot-home.vue
      let bindInterval = setInterval(() => {
        if (!bindParam.device_sn) {
          // No need to call getRemoteControllerSN() again
          console.log('[CLOUD-SAMPLE] No device SN in binding parameters, cannot bind');
          return;
        }
        
        // Bind request using token from localStorage - matches pilot-home.vue
        const headers = {};
        headers['x-auth-token'] = localStorage.getItem(ELocalStorageKey.Token);
        
        axios.post(BIND_DEVICE_URL(bindParam.device_sn), bindParam, { headers })
          .then(bindRes => {
            if (bindRes.data.code !== 0) {
              console.error('[CLOUD-SAMPLE] Binding error:', bindRes.data.message);
            } else {
              clearInterval(bindInterval);
            }
          }).catch(async err => {
            console.error('[CLOUD-SAMPLE] Binding error:', err);
            await handleApiError(err, navigate);
          });
      }, 2000);
      
      // Get device info after a delay
      setTimeout(getDeviceInfo, 3000);
    } else {
      setThingState(EStatusValue.DISCONNECT);
    }
    
    refreshStatus();
  };
  
  /**
   * WebSocket connection callback - exactly as in pilot-home.vue wsConnectCallback function
   */
  const wsConnectCallback = async (arg) => {
    setWsState(arg ? EStatusValue.CONNECTED : EStatusValue.DISCONNECT);
  };
  
  /**
   * Show module status drawer - exactly as in pilot-home.vue showStatus function
   */
  const showStatus = () => {
    let statusInterval = setInterval(() => {
      refreshStatus();
      if (!drawerVisible) {
        clearInterval(statusInterval);
      }
    }, 2000);
    
    setDrawerVisible(true);
  };
  
  /**
   * Confirm exit - exactly as in pilot-home.vue confirmAgain function
   */
  const confirmExit = () => {
    setExitVisible(true);
  };
  
  /**
   * Cancel exit - exactly as in pilot-home.vue onBack function
   */
  const cancelExit = () => {
    setExitVisible(false);
  };
  
  /**
   * Handle exit - exactly as in pilot-home.vue onExit function
   */
  const handleExit = () => {
    localStorage.clear();
    djiBridge.stopwebview();
  };
  
  /**
   * Handle logout
   */
  const handleLogout = () => {
    localStorage.removeItem(ELocalStorageKey.Token);
    localStorage.removeItem(ELocalStorageKey.Username);
    navigate('/cloud-sample-login');
  };
  
  /**
   * Verify if token is valid by making a request to the server
   * @returns {Promise<boolean>} Whether the token is valid
   */
  const verifyTokenValidity = async (navigateFunc) => {
    // Try to get token from localStorage first
    const token = localStorage.getItem(ELocalStorageKey.Token);
    
    // If no token is found, return false
    if (!token) {
      console.error('[CLOUD-SAMPLE] No token found in localStorage');
      return false;
    }
    
    try {
      console.log('[CLOUD-SAMPLE] Verifying token validity with backend request');
      
      // Make a request to the user info endpoint to check token validity
      const headers = {};
      headers[ELocalStorageKey.Token] = token;
      
      console.log('[CLOUD-SAMPLE] Making user info request with headers:', headers);
      console.log('[CLOUD-SAMPLE] Request URL:', USER_INFO_URL);
      
      const response = await axios.get(USER_INFO_URL, { headers });
      
      console.log('[CLOUD-SAMPLE] User info response:', response.data);
      
      // If response is success WITH CODE 0, token is valid
      if (response.status === 200 && response.data.code === 0) {
        console.log('[CLOUD-SAMPLE] Token is valid (code=0)');
        
        // Load API component if not already loaded
        if (!djiBridge.isComponentLoaded(EComponentName.Api)) {
          // Set API component parameters with token
          djiBridge.setComponentParam(EComponentName.Api, {
            host: window.location.origin,
            token: token
          });
          
          // Load API component
          const apiLoaded = djiBridge.loadComponent(
            EComponentName.Api,
            djiBridge.getComponentParam(EComponentName.Api)
          );
          
          if (!apiLoaded) {
            console.error('[CLOUD-SAMPLE] Failed to load API component');
            return false;
          }
          
          // Set token in DJI Pilot using apiSetToken
          if (token && token.trim() !== '') {
            console.log(`[CLOUD-SAMPLE] Setting token using djiBridge.setToken: ${token.substring(0, 10)}...`);
            const setResult = djiBridge.setToken(token);
            console.log('[CLOUD-SAMPLE] setToken result:', setResult);
          }
        }
        
        return true;
      } else {
        console.log('[CLOUD-SAMPLE] Token validation failed: Status OK but code not 0:', response.data.code);
        
        // Token is invalid, redirect to login
        localStorage.removeItem(ELocalStorageKey.Token);
        navigateFunc('/cloud-sample-login');
        return false;
      }
    } catch (error) {
      console.error('[CLOUD-SAMPLE] Error validating token:', error);
      console.log('[CLOUD-SAMPLE] Error response:', error.response ? error.response.data : 'No response data');
      
      // If 401 unauthorized error, token is expired or invalid
      if (error.response && error.response.status === 401) {
        console.log('[CLOUD-SAMPLE] Token is invalid or expired (401)');
        localStorage.removeItem(ELocalStorageKey.Token);
        navigateFunc('/cloud-sample-login');
      }
      
      return false;
    }
  };
  
  return (
    <Container fluid className="dashboard-container">
      <Row className="dashboard-header mb-4">
        <Col md={6}>
          <h2>
            <FontAwesomeIcon icon={faCloud} className="mr-2" /> Cloud Sample Dashboard
          </h2>
          {username && <p className="text-muted">Logged in as: {username}</p>}
        </Col>
        <Col md={6} className="text-end">
          <Button variant="primary" onClick={refreshStatus} className="me-2">
            <FontAwesomeIcon icon={faSync} /> Refresh
          </Button>
          <Button variant="outline-danger" onClick={handleLogout}>
            <FontAwesomeIcon icon={faSignOutAlt} /> Logout
          </Button>
        </Col>
      </Row>
      
      {/* Error alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {/* Loading indicator */}
      {loading ? (
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Initializing dashboard...</p>
        </div>
      ) : (
        <Row>
          {/* Left Sidebar - mirroring pilot-home.vue */}
          <Col md={5}>
            <Card className="mb-4">
              <Card.Header>
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon icon={faCloud} className="me-2" />
                  <h5 className="mb-0">Workspace Status</h5>
                </div>
              </Card.Header>
              <Card.Body>
                <div className="d-flex align-items-center mb-3">
                  <div className="flex-grow-1">
                    <h6 className="mb-0">{workspaceName}</h6>
                    <small className="text-muted">Workspace Name</small>
                  </div>
                  <Button variant="link" onClick={showStatus}>
                    <FontAwesomeIcon icon={faChevronRight} />
                  </Button>
                </div>
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon 
                    icon={faSync} 
                    className={thingState === EStatusValue.CONNECTED ? 'text-primary' : 'text-muted'} 
                  />
                  <span className="ms-2">{thingState}</span>
                </div>
              </Card.Body>
            </Card>
            
            {/* Exit Button - exactly as in pilot-home.vue */}
            <Button 
              variant="danger" 
              className="exit-button"
              onClick={confirmExit}
            >
              Exit
            </Button>
          </Col>
          
          {/* Right Content - mirroring pilot-home.vue */}
          <Col md={7}>
            {/* Device Information */}
            <div className="mb-4">
              <h6 className="text-muted mb-2">Serial Number</h6>
              <Card>
                <Card.Body>
                  <Row className="mb-3">
                    <Col md={8}>Remote Control SN</Col>
                    <Col md={4} className="text-end">
                      <span className="text-muted">{device.data.gateway_sn}</span>
                    </Col>
                  </Row>
                  {device.data.online_status && device.data.sn && (
                    <Row>
                      <Col md={8}>Aircraft SN</Col>
                      <Col md={4} className="text-end">
                        <span className="text-muted">{device.data.sn}</span>
                      </Col>
                    </Row>
                  )}
                </Card.Body>
              </Card>
            </div>
            
            {/* Settings */}
            <div className="mb-4">
              <h6 className="text-muted mb-2">Settings</h6>
              <Card>
                <Card.Body>
                  {device.data.online_status && device.data.sn && (
                    <Row className="mb-3">
                      <Col md={20}>
                        <div className="d-flex justify-content-between align-items-center">
                          <span>Device Binding</span>
                          <div>
                            <span className="text-muted me-2">
                              {device.data.bound_status ? 'Aircraft bound' : 'Aircraft not bound'}
                            </span>
                            <FontAwesomeIcon icon={faChevronRight} />
                          </div>
                        </div>
                      </Col>
                    </Row>
                  )}
                  <Row className="mb-3">
                    <Col md={20}>
                      <div className="d-flex justify-content-between align-items-center">
                        <span>Media File Upload</span>
                        <FontAwesomeIcon icon={faChevronRight} />
                      </div>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={20}>
                      <div className="d-flex justify-content-between align-items-center">
                        <span>Livestream Manually</span>
                        <FontAwesomeIcon icon={faChevronRight} />
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={20}>
                      <div className="d-flex justify-content-between align-items-center">
                        <span>Open 3rd Party APP</span>
                        <FontAwesomeIcon icon={faChevronRight} />
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>
      )}
      
      {/* Module Status Drawer */}
      <div className={`module-drawer ${drawerVisible ? 'show' : ''}`}>
        <div className="drawer-header">
          <h5>Module State</h5>
          <Button variant="link" onClick={() => setDrawerVisible(false)}>
            <FontAwesomeIcon icon={faSignOutAlt} />
          </Button>
        </div>
        <div className="drawer-body">
          {modules.map((m) => (
            <div key={m.name} className="module-item">
              <div className="module-name">{m.name}</div>
              <div className="module-actions">
                <Badge bg={m.state === EStatusValue.CONNECTED ? 'success' : 'danger'} className="me-2">
                  {m.state}
                </Badge>
                <Button variant="primary" size="sm" onClick={() => moduleInstall(m)} className="me-2">
                  Install
                </Button>
                <Button variant="danger" size="sm" onClick={() => moduleUninstall(m)}>
                  Uninstall
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Exit Confirmation Modal */}
      <div className={`modal ${exitVisible ? 'show' : ''}`}>
        <div className="modal-content">
          <p>Data will not be synchronized between DJI Pilot and this server after exiting.</p>
          <div className="modal-footer">
            <Button variant="secondary" onClick={cancelExit}>Cancel</Button>
            <Button variant="danger" onClick={handleExit}>Exit</Button>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default CloudSamplePilotDashboard; 